import { CURRENT_PRICING, type Pricing } from '@/api/consts'
import { type OfferId } from '@/api/schema'
import { Row } from '@/app/components/Flex/Flex'
import { OfferItem, type OfferItemProps } from '@/app/components/OfferV2/OfferItem/OfferItem'
import { useSession } from '@/app/state/session'
import type { FunctionalComponent } from 'preact'
import { useShallow } from 'zustand/shallow'

const FIRST_PAYMENT_OFFERS: OfferId[] = ['one', 'week']
const DEFAULT_OFFERS: OfferId[] = ['one', 'week', 'full']

const PANEL_TYPES: OfferItemProps['type'][] = ['violet', 'yellow', 'pink']

export const Offers: FunctionalComponent<{
  activeId: OfferId
  onChange: (activeId: OfferId) => void
}> = props => {
  const { pricingType, hasLoyaltyDiscount } = useSession(useShallow(s => ({
    pricing: s.user?.pricing ?? CURRENT_PRICING,
    pricingType: s.user?.pricingType ?? 'current',
    hasLoyaltyDiscount: s.user?.hasLoyaltyDiscount,
  })))

  const showDefaultOffers = pricingType === 'legacy'
    ? true
    : hasLoyaltyDiscount

  const offerIds = showDefaultOffers ? DEFAULT_OFFERS : FIRST_PAYMENT_OFFERS

  return (
    <Row gap={15}>
      {offerIds.map(offerId => (
        <Offer
          offerId={offerId}
          active={props.activeId === offerId}
          onClick={() => {
            props.onChange(offerId)
          }}
        />
      ))}
    </Row>
  )
}

/**
 * Возвращает "обычную" цену тарифа — сколько бы стоило это же количество
 * сообщений, если покупать поштучно по цене младшего тарифа.
 * Возвращает null, если скидки нет (или цена не меньше обычной).
 */
export function getOriginalPrice(offerId: OfferId, baseOfferId: OfferId, pricing: Pricing): number | null {
  const basePricePerMessage = pricing.amount[baseOfferId] / pricing.messages[baseOfferId]
  const actualPrice = pricing.amount[offerId]
  const messages = pricing.messages[offerId]
  const originalPrice = messages * basePricePerMessage

  // Если фактическая цена не дешевле — не показываем зачёркнутую
  if (originalPrice <= actualPrice) return null

  return Math.round(originalPrice)
}

export function getDiscountPercent(offerId: OfferId, originalPrice: number, pricing: Pricing): number | null {
  const actualPrice = pricing.amount[offerId]

  if (originalPrice <= actualPrice) return null

  return Math.round(((originalPrice - actualPrice) / originalPrice) * 100)
}

export function getDiscount(offerId: OfferId, pricing: Pricing): number | null {
  const price = getOriginalPrice('week', 'one', pricing)
  return price ? getDiscountPercent(offerId, price, pricing) : null
}

export function Offer(props: {
  offerId: OfferId
  active?: boolean
  onClick?: () => void
}) {
  const { pricing, pricingType, hasLoyaltyDiscount } = useSession(useShallow(s => ({
    pricing: s.user?.pricing ?? CURRENT_PRICING,
    pricingType: s.user?.pricingType ?? 'current',
    hasLoyaltyDiscount: s.user?.hasLoyaltyDiscount,
  })))

  const index = props.offerId === 'week' ? 1 : props.offerId === 'full' ? 2 : 0

  const showDefaultOffers = pricingType === 'legacy'
    ? true
    : hasLoyaltyDiscount

  const offerIds = showDefaultOffers ? DEFAULT_OFFERS : FIRST_PAYMENT_OFFERS
  const baseOfferId = offerIds[0]

  const badge: OfferItemProps['badgeType'] | null = index === 0
    ? null
    : index === 1
    ? 'orangeGr'
    : index === 2
    ? 'pinkGr'
    : null

  const originalPrice = index > 0
    ? getOriginalPrice(props.offerId, baseOfferId, pricing)
    : null
  const discountPercent = originalPrice
    ? getDiscountPercent(props.offerId, originalPrice, pricing)
    : null

  return (
    <OfferItem
      clickable={!!props.onClick}
      active={props.active}
      amount={pricing.amount[props.offerId]}
      messages={pricing.messages[props.offerId]}
      type={PANEL_TYPES[index]}
      badge={badge ? `Выгоднее на ${discountPercent}%` : null}
      badgeType={badge}
      originalPrice={originalPrice}
      onClick={props.onClick}
    />
  )
}
